import Button from '@rio-cloud/rio-uikit/Button';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { useIntl } from 'react-intl';

import type { SmartRouteStage } from '#features/tourCreate/hooks/useSmartRoute.ts';

export const SmartRouteProgress = ({
    stage,
    onCancel,
    onRetry,
}: {
    stage?: SmartRouteStage;
    onCancel: () => void;
    onRetry: () => void;
}) => {
    const intl = useIntl();
    if (!stage) {
        return null;
    }
    const message = (key: string) => intl.formatMessage({ id: `intl-msg:ordercom-cloud-frontend.smart-route.${key}` });
    const steps = ['preparing', 'calculating', 'preview'] as const;
    const complete = stage === 'complete';
    const active = complete ? steps.length : stage === 'error' ? -1 : steps.indexOf(stage);
    return (
        <Dialog
            show
            bsSize='sm'
            title={message('title')}
            onClose={onCancel}
            body={
                <div className='display-flex flex-column gap-20 padding-y-10' aria-live='polite'>
                    {stage === 'error' ? (
                        <div role='alert' className='alert alert-danger margin-0'>
                            {message('error')}
                        </div>
                    ) : (
                        <>
                            <div className='border rounded bg-lightest display-flex flex-column align-items-center text-center gap-10 shadow-subtle padding-25'>
                                {complete ? (
                                    <span
                                        className='uikit-icon icon-check-badge text-color-success text-size-h1'
                                        aria-hidden='true'
                                    />
                                ) : (
                                    <Spinner isDoubleSized />
                                )}
                                <div className='text-size-16 text-medium'>
                                    {message(complete ? 'title' : 'calculating')}
                                </div>
                            </div>
                            <div>
                                <div className='text-size-12 text-color-dark margin-bottom-5'>{message('title')}</div>
                                <div
                                    className='progress margin-0'
                                    role='progressbar'
                                    aria-label={message('title')}
                                    aria-valuenow={complete ? 100 : undefined}
                                >
                                    <div
                                        className={`progress-bar bg-success width-100pct${complete ? '' : ' progress-bar-striped progress-bar-animated'}`}
                                    />
                                </div>
                            </div>
                            <ol className='list-unstyled display-flex flex-column gap-10 margin-0'>
                                {steps.map((step, index) => (
                                    <li
                                        key={step}
                                        className='display-flex align-items-center gap-10'
                                        aria-current={index === active ? 'step' : undefined}
                                    >
                                        <span
                                            aria-hidden='true'
                                            className='width-20 flex-shrink-0 text-size-20 display-flex align-items-center justify-content-center'
                                        >
                                            {index < active ? (
                                                <span className='uikit-icon icon-check-badge text-color-success' />
                                            ) : index === active ? (
                                                <Spinner isInline />
                                            ) : (
                                                <span className='uikit-icon icon-time text-color-light' />
                                            )}
                                        </span>
                                        <span
                                            className={
                                                index === active
                                                    ? 'text-medium text-color-darker'
                                                    : index < active
                                                      ? 'text-color-dark'
                                                      : 'text-color-light'
                                            }
                                        >
                                            {message(step)}
                                        </span>
                                    </li>
                                ))}
                            </ol>
                        </>
                    )}
                </div>
            }
            footer={
                <div className='display-flex justify-content-end gap-10'>
                    <Button bsStyle='default' onClick={onCancel}>
                        {intl.formatMessage({ id: 'intl-msg:common-message.cancel' })}
                    </Button>
                    {stage === 'error' && (
                        <Button bsStyle='primary' onClick={onRetry}>
                            {intl.formatMessage({ id: 'intl-msg:common-message.error.generic.retry' })}
                        </Button>
                    )}
                </div>
            }
        />
    );
};
